import { useState, type ReactNode } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryError } from '@/components/ui/error-boundary';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { extractErrorMessage } from '@/lib/errors';
import { useClearPaymentDetails, usePaymentDetails } from '@/lib/employee-finance';
import { formatIban } from '@/features/people/lib/payment-details-form';
import { IconActionButton } from './icon-action-button';
import { PaymentDetailsDialog } from './payment-details-dialog';

interface PaymentDetailsBlockProps {
  employeeId: string;
  /** `useCanViewPaymentDetails` — without it the block is not rendered and nothing is requested. */
  canView: boolean;
  /** `useCanEditPaymentDetails` — view and edit together, as PUT and DELETE require. */
  canEdit: boolean;
}

function BlockCard({ actions, children }: { actions?: ReactNode; children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border/50 bg-muted/30 px-5 py-3">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Payment details (FOP)
        </h3>
        {actions && <div className="flex items-center gap-1.5">{actions}</div>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

/**
 * The employee's FOP payment details on the Documents tab [HW-1008]. Fetched
 * apart from the profile and only for a reader the API would answer, so a
 * reader without the right never sends the request.
 */
export function PaymentDetailsBlock({ employeeId, canView, canEdit }: PaymentDetailsBlockProps) {
  const { data: details, isLoading, error } = usePaymentDetails(employeeId, { enabled: canView });
  const clear = useClearPaymentDetails(employeeId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  if (!canView) return null;

  if (isLoading) {
    return (
      <BlockCard>
        <div className="space-y-3">
          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-5 w-1/3" />
        </div>
      </BlockCard>
    );
  }

  if (error) {
    return (
      <BlockCard>
        <QueryError error={error} />
      </BlockCard>
    );
  }

  const handleClear = () =>
    clear.mutate(undefined, {
      onSuccess: () => {
        toast.success('Payment details cleared');
        setConfirmClear(false);
      },
      onError: (err) => toast.error(extractErrorMessage(err, 'Failed to clear payment details')),
    });

  const rows: { label: string; value: string | null }[] = details
    ? [
        { label: 'FOP name', value: details.fopName },
        { label: 'IBAN', value: details.iban && formatIban(details.iban) },
        { label: 'Tax ID (RNOKPP)', value: details.taxId },
        { label: 'SWIFT / BIC', value: details.swift },
        { label: 'Currency', value: details.currency },
        { label: 'Registration address', value: details.registrationAddress },
        { label: 'KVED', value: details.kved.length ? details.kved.join(', ') : null },
      ]
    : [];

  return (
    <>
      <BlockCard
        actions={
          canEdit &&
          details && (
            <>
              <IconActionButton
                icon={Pencil}
                tooltip="Edit payment details"
                size="sm"
                onClick={() => setDialogOpen(true)}
              />
              <IconActionButton
                icon={Trash2}
                tooltip="Clear payment details"
                size="sm"
                variant="destructive"
                onClick={() => setConfirmClear(true)}
              />
            </>
          )
        }
      >
        {details ? (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-[180px_1fr]">
            {rows.map(({ label, value }) => (
              <div key={label} className="contents">
                <dt className="text-sm text-muted-foreground">{label}</dt>
                <dd className="text-sm wrap-anywhere">
                  {value || <span className="text-muted-foreground">—</span>}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <div className="flex flex-col items-start gap-3">
            <p className="text-sm text-muted-foreground">No payment details yet.</p>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 cursor-pointer"
                onClick={() => setDialogOpen(true)}
              >
                <Plus className="h-3.5 w-3.5" />
                Add payment details
              </Button>
            )}
          </div>
        )}
      </BlockCard>

      {canEdit && (
        <>
          <PaymentDetailsDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            employeeId={employeeId}
            details={details ?? null}
          />
          <ConfirmDialog
            open={confirmClear}
            title="Clear payment details?"
            description="Every FOP field of this employee is removed. This cannot be undone."
            confirmLabel="Clear"
            destructive
            isPending={clear.isPending}
            onOpenChange={setConfirmClear}
            onConfirm={handleClear}
          />
        </>
      )}
    </>
  );
}
